import React, { useMemo } from 'react'
import { connect, useSelector } from 'react-redux'
import { bindActionCreators, Dispatch } from 'redux'
import clsx from 'clsx'
import _toNumber from 'lodash/toNumber'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import Typography from '@mui/material/Typography'
import { RootActionType, RootState } from 'duck'
import { actions, Utils, selectors, BASE_POINT_TO_PERCENT } from '../../duck'
import { ReduxProps } from 'utils/Types'
import Button from 'components/Button'
import Tooltip from 'components/Tooltip'
import TextFieldNftWallet from './TextFieldNftWallet'
import stylesGlobal from 'stylesGlobal.module.scss'
import styles from '../NFTSellForm.module.scss'

const mapDispatchToProps = (dispatch: Dispatch<RootActionType>) =>
  bindActionCreators(
    {
      updateFormData: actions.updateFormData
    },
    dispatch
  )

type PaymentDetailsProps = ReduxProps<typeof mapDispatchToProps>

const PaymentDetails: React.FC<PaymentDetailsProps> = props => {
  const { updateFormData } = props

  const royalties = useSelector((state: RootState) => selectors.formData(state).royalties)
  const royaltiesError = useSelector((state: RootState) => selectors.formErrors(state).royalties)
  const royaltiesLength = royalties.length
  const disabled = useSelector((state: RootState) => selectors.isResubmitForm(state))

  const royaltiesAdjusted = useMemo(
    () =>
      royalties.map(value => ({
        ...value,
        initial_sale_fee_in_bp: value.initial_sale_fee_in_bp * BASE_POINT_TO_PERCENT
      })),
    [royalties]
  )

  return !disabled ? (
    <div className={clsx('mb-11', stylesGlobal.BorderBottomOnSurface)}>
      <Typography variant="h6" className="mb-7">
        Payment Details
      </Typography>

      {royaltiesAdjusted.map((value, index) => (
        <div key={index} className="mb-4 flex">
          <TextFieldNftWallet
            label={`Wallet Address ${index + 1}`}
            value={value}
            hideInputPercentage={royaltiesLength <= 1}
            error={Boolean(royaltiesError)}
            onChange={event => {
              const newValue = [...royalties]
              newValue[index] = {
                ...newValue[index],
                address: event.target.value
              }
              updateFormData({ royalties: newValue })
            }}
            onChangePercent={event => {
              const newValue = [...royalties]
              newValue[index] = {
                ...newValue[index],
                initial_sale_fee_in_bp: _toNumber(event.target.value) / BASE_POINT_TO_PERCENT
              }
              updateFormData({ royalties: newValue })
            }}
            disabled={disabled}
          />

          {royaltiesLength > 1 ? (
            <div className={styles.ButtonWalletRemove}>
              <Button
                disabled={disabled}
                className="h-full"
                onClick={() => {
                  const newValue = [...royalties]
                  newValue.splice(index, 1)
                  updateFormData({ royalties: Utils.adjustAfterRemoveRoyalty(newValue) })
                }}
                color="error"
                iconSquare
                icon
              >
                <CloseIcon />
              </Button>
            </div>
          ) : null}
        </div>
      ))}

      <div className="mb-2">
        {royaltiesLength < 5 ? (
          <div className="flex items-center">
            <div>
              <Button
                disabled={disabled}
                color="secondary"
                startIcon={<AddIcon />}
                onClick={() => {
                  updateFormData({ royalties: Utils.addRoyalty(royalties) })
                }}
              >
                ADD ANOTHER WALLET
              </Button>
            </div>

            <Tooltip
              className="ml-2 text-white-med-on-dark"
              title={
                <Typography variant="caption">
                  <span className="mb-4 block">
                    Divide future payments across multiple wallet addresses. You can adjust the
                    percentage of each transaction per wallet.
                  </span>
                  Limited to 5 wallets only.
                </Typography>
              }
            >
              <HelpOutlineIcon className="text-base" />
            </Tooltip>
          </div>
        ) : null}

        {royaltiesError ? (
          <Typography className={styles.HelperText} color="error">
            {royaltiesError}
          </Typography>
        ) : null}
      </div>
    </div>
  ) : null
}

export default connect(null, mapDispatchToProps)(PaymentDetails)
